import React, {useEffect} from "react";
import {Alert, Button, Card, CardContent, Grid, IconButton, Toolbar, Typography} from "@mui/material";
import {ArrowBack, DeleteOutline} from "@mui/icons-material";
import {RootGrid, HeaderTypography} from "./taxonomy";
import {
    deleteAllProducts as utilDeleteAllProducts,
    deleteAllSupplierProducts as utilDeleteAllSupplierProducts,
    getProductCount as utilGetProductCount,
    utilGetPartners
} from "../../../../../utils/utils";
import EnhancedErrorDialog from "../../../../../components/EnhancedErrorDialog";
import EnhancedConfirmDialog from "../../../../../components/EnhancedConfirmDialog";
import EnhancedSelect from "../../../../../components/EnhancedSelect";
import UpdatingDialog from "../../../../../components/Dialogs/UpdatingDialog";
import ResultDialog from "../../../../../components/ResultDialog";
import {pocPrimary, pageContentGap} from "../../../../../theme";
import Auth from "../../../../../auth";

export const DELETE_ALL_PRODUCTS_NOTE = "This will delete all products in My Catalog. This cannot be undone. Deleting all products will create an event which contains a backup file.";
export const DELETE_SUPPLIER_PRODUCTS_NOTE = "This will delete all products in a selected Supplier Catalog. This cannot be undone. Deleting products will create an event which contains a backup file.";

interface ErrorState {
    open: boolean;
    errorHeader: string;
    errorMessage: string;
}

interface SupplierOption {
    label: string;
    value: number | string;
}

interface PendingDelete {
    type: "all" | "supplier";
    count: number;
    supplier?: SupplierOption;
}

interface BulkActionsProps {
    setTitle?: (title: string, flag?: boolean) => void;
    location: {
        key?: string;
    };
    history: {
        push: (path: string) => void;
        goBack: () => void;
    };
}

const iconButtonSx = () => ({
    backgroundColor: "white",
    border: "none",
    borderRadius: "6px",
    "& svg": { color: "rgba(0, 0, 0, 0.54)" }
});

const formatCount = (count: number) => count.toLocaleString();

export default function BulkActions(props: BulkActionsProps) {
    const {setTitle} = props;
    const hasInventoryManagePermission = Auth.hasPermission("inventory/manage");
    const [error, setError] = React.useState<ErrorState>({open: false, errorHeader: "", errorMessage: ""});
    const [suppliers, setSuppliers] = React.useState<SupplierOption[]>([]);
    const [selectedSupplier, setSelectedSupplier] = React.useState<SupplierOption | null>(null);
    const [pendingDelete, setPendingDelete] = React.useState<PendingDelete | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [deleting, setDeleting] = React.useState(false);
    const [resultMessage, setResultMessage] = React.useState("");

    useEffect(() => {
        if (setTitle) {
            setTitle('POC Bulk Actions', true);
        }
        const getSuppliers = async () => {
            const partners = await utilGetPartners();
            if (partners.errorMessage) {
                setError(partners);
                return;
            }
            setSuppliers(partners
                .map((partner: any) => ({label: partner.CompanyName, value: partner.Id}))
                .sort((a: SupplierOption, b: SupplierOption) => (a.label || "").localeCompare(b.label || "")));
        }
        getSuppliers();
        // eslint-disable-next-line
    }, []);

    const denyIfNoPermission = () => {
        if (hasInventoryManagePermission) return false;
        setError({
            open: true,
            errorHeader: "Permission Denied",
            errorMessage: "You do not have permission to perform this action."
        });
        return true;
    }

    // The count is fetched on click so the confirmation reflects the catalog as it is now.
    const requestDelete = async (type: PendingDelete["type"]) => {
        if (denyIfNoPermission()) return;
        if (type === "supplier" && !selectedSupplier) return;

        setLoading(true);
        const count = await utilGetProductCount(type === "supplier" ? selectedSupplier.value : undefined);
        setLoading(false);
        if (count?.errorMessage) {
            setError(count);
            return;
        }
        setPendingDelete({type, count, supplier: type === "supplier" ? selectedSupplier : undefined});
    }

    const confirmDelete = async () => {
        if (!pendingDelete || denyIfNoPermission()) return;

        setDeleting(true);
        const result = pendingDelete.type === "all"
            ? await utilDeleteAllProducts()
            : await utilDeleteAllSupplierProducts(pendingDelete.supplier.value);
        setDeleting(false);
        if (result !== true) {
            setError(result);
            return;
        }
        setResultMessage(pendingDelete.type === "all"
            ? `${formatCount(pendingDelete.count)} products have been deleted.`
            : `${formatCount(pendingDelete.count)} products have been deleted for ${pendingDelete.supplier.label}.`);
        if (pendingDelete.type === "supplier") setSelectedSupplier(null);
    }

    const confirmMessage = pendingDelete == null ? "" : pendingDelete.type === "all"
        ? `Are you sure you want to delete ${formatCount(pendingDelete.count)} products from My Catalog? This action cannot be undone.`
        : `Are you sure you want to delete ${formatCount(pendingDelete.count)} products for ${pendingDelete.supplier.label}? This action cannot be undone.`;

    return (
        <RootGrid container
              style={{gap: pageContentGap, padding: "24px 14px 24px 14px", position: "relative"}}>
            <Grid container justifyContent={"space-between"} direction={"row"}>
                <Grid container justifyContent={"flex-start"} alignItems={"center"} style={{gap: pageContentGap, width: "100%"}}>
                    <IconButton
                        sx={iconButtonSx}
                        style={{height: "36px", width: "36px", position: 'relative', zIndex: 1}}
                        onClick={(e) => {
                            e.preventDefault();
                            if (props.location.key === undefined)
                                props.history.push("/productonboardingcenter/catalog");
                            else
                                props.history.goBack();
                        }}
                        size="large">
                        <ArrowBack style={{height: "18px", width: "18px"}}/>
                    </IconButton>
                    <HeaderTypography>
                        Bulk Actions
                    </HeaderTypography>
                </Grid>
            </Grid>
            <Grid size={12}>
                <Card>
                    <Toolbar className='lbtoolbar' style={{background: pocPrimary}}>
                        Delete All Products
                    </Toolbar>
                    <Alert severity={"info"} style={{margin: "1em 1em 0 1em"}}>
                        <Typography variant={"body1"} style={{textAlign: "left"}}>
                            {DELETE_ALL_PRODUCTS_NOTE}
                        </Typography>
                    </Alert>
                    <CardContent style={{textAlign: "left"}}>
                        <Button variant="outlined" color="error" startIcon={<DeleteOutline/>}
                                disabled={loading || !hasInventoryManagePermission}
                                onClick={() => requestDelete("all")}>
                            Delete All Products
                        </Button>
                    </CardContent>
                </Card>
            </Grid>
            <Grid size={12}>
                <Card>
                    <Toolbar className='lbtoolbar' style={{background: pocPrimary}}>
                        Delete Supplier Products
                    </Toolbar>
                    <Alert severity={"info"} style={{margin: "1em 1em 0 1em"}}>
                        <Typography variant={"body1"} style={{textAlign: "left"}}>
                            {DELETE_SUPPLIER_PRODUCTS_NOTE}
                        </Typography>
                    </Alert>
                    <CardContent>
                        <Grid container direction={"column"} alignItems={"flex-start"} style={{gap: pageContentGap, maxWidth: "900px"}}>
                            <Grid style={{width: "100%"}}>
                                <EnhancedSelect
                                    label="Supplier"
                                    placeholder="Select a supplier..."
                                    options={suppliers}
                                    value={selectedSupplier}
                                    onChange={(value: SupplierOption | null) => setSelectedSupplier(value)}
                                    disabled={!hasInventoryManagePermission}
                                />
                            </Grid>
                            <Button variant="outlined" color="error" startIcon={<DeleteOutline/>}
                                    disabled={loading || !hasInventoryManagePermission || !selectedSupplier}
                                    onClick={() => requestDelete("supplier")}>
                                Delete Supplier Products
                            </Button>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>
            <EnhancedConfirmDialog open={pendingDelete !== null}
                                   setOpen={(open: boolean) => { if (!open) setPendingDelete(null); }}
                                   title={pendingDelete?.type === "supplier" ? "Delete Supplier Products?" : "Delete All Products?"}
                                   message={confirmMessage}
                                   type={"delete"}
                                   action={(confirmed: boolean) => {
                                       if (confirmed) confirmDelete();
                                   }}
            />
            <UpdatingDialog open={deleting} setOpen={setDeleting}
                            title={"Deleting products..."}
                            subtitle={"Please wait while we delete the products."}/>
            <ResultDialog open={resultMessage !== ""} onClose={() => setResultMessage("")}
                          title={"Products Deleted"} message={resultMessage}/>
            <EnhancedErrorDialog open={error.open} onClose={() => setError({...error, open: false})}
                                 errorHeader={error.errorHeader} errorMessage={error.errorMessage}/>
        </RootGrid>
    );
}
